import { lazy, Suspense, useEffect } from "react";
import { LoadingSpinner } from "./components/ui/LoadingSpinner";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import {
  ErrorBoundary,
  setupGlobalErrorHandlers,
  RouteErrorBoundary,
} from "./components/ErrorBoundary";
import { WritersLogicLayout } from "./components/layouts";
import { IS_DOCS_HOST, docsTo } from "./lib/docsBase";
import { BLOG_ENABLED } from "./content/blogPosts/enabled";
import {
  initializeAnalytics,
  trackPageView,
  useEngagementTracking,
  useScrollDepthTracking,
} from "./services/googleAnalytics";

// Lazy-load non-critical App-shell components (not needed at first paint)
const IconSprite = lazy(() =>
  import("./components/icons/Icon").then((m) => ({ default: m.IconSprite })),
);
const ToastContainer = lazy(() =>
  import("./components/Toast/ToastContainer").then((m) => ({
    default: m.ToastContainer,
  })),
);
const ConfirmDialog = lazy(() =>
  import("./components/ui/ConfirmDialog").then((m) => ({
    default: m.ConfirmDialog,
  })),
);
const OfflineBanner = lazy(() =>
  import("./components/OfflineBanner").then((m) => ({
    default: m.OfflineBanner,
  })),
);

// Set up global error handlers for uncaught errors
if (typeof window !== "undefined") {
  setupGlobalErrorHandlers();
}

// Lazy load landing pages
const HomePage = lazy(() => import("./pages/HomePage"));
const FheLiteDemoPage = lazy(() => import("./pages/FheLiteDemoPage").then(m => ({ default: m.FheLiteDemoPage })));
const LandingPage = lazy(() =>
  import("./pages/LandingPage").then((m) => ({ default: m.LandingPage })),
);

// Lazy load heavy pages (editor, ML runtime, etc.)
const NotFoundPage = lazy(() =>
  import("./pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);

// Lazy load blog pages
const BlogIndex = lazy(() =>
  import("./pages/BlogIndex").then((m) => ({ default: m.BlogIndex })),
);
const BlogPost = lazy(() =>
  import("./pages/BlogPost").then((m) => ({ default: m.BlogPost })),
);

// Lazy load legal pages
const PrivacyPage = lazy(() =>
  import("./pages/PrivacyPage").then((m) => ({ default: m.PrivacyPage })),
);
const TermsPage = lazy(() =>
  import("./pages/TermsPage").then((m) => ({ default: m.TermsPage })),
);
const DocsPage = lazy(() => import("./pages/DocsPage"));

// Lazy load about page
const AboutPage = lazy(() =>
  import("./pages/AboutPage").then((m) => ({ default: m.AboutPage })),
);

// Lazy load compliance checkpoint page
const ComplianceCheckpointPage = lazy(() =>
  import("./pages/ComplianceCheckpointPage").then((m) => ({
    default: m.ComplianceCheckpointPage,
  })),
);

// Lazy load executive summary & sample report
const ExecutiveSummaryPage = lazy(() => import("./pages/ExecutiveSummaryPage"));
const SampleAuthorshipReportPage = lazy(
  () => import("./pages/SampleAuthorshipReportPage"),
);

// Lazy load LOI page
const LOIPage = lazy(() => import("./pages/LOIPage"));
const MOUPage = lazy(() => import("./pages/MOUPage"));
const ChronographLandingPage = lazy(
  () => import("./pages/ChronographLandingPage"),
);

// Lazy load licensing page
const LicensingPage = lazy(() => import("./pages/LicensingPage"));

// Lazy load developers page
const DevelopersPage = lazy(() => import("./pages/DevelopersPage"));

// Lazy load protocol page
const ProtocolPage = lazy(() => import("./pages/ProtocolPage"));

// Lazy load security model page
const SecurityModelPage = lazy(() => import("./pages/SecurityModelPage"));

// Lazy load ZWC watermark page
const ZwcWatermarkPage = lazy(() => import("./pages/ZwcWatermarkPage"));

// Lazy load the free client-side sign & verify tool
const SignPage = lazy(() => import("./pages/SignPage"));

// ARK identifier and signed-assertion verifier
const ArkVerifyPage = lazy(() => import("./pages/ArkVerifyPage"));

// Auth magic-link callback handler
const CallbackPage = lazy(() => import("./pages/CallbackPage"));

// C2PA Certification Authority public repository page
const CaRepositoryPage = lazy(() => import("./pages/CaRepositoryPage"));

// Lazy load pricing page
const PricingPage = lazy(() => import("./pages/PricingPage"));

// OnRecord Studio product page and checkout-return states
const OnRecordPage = lazy(() => import("./pages/OnRecordPage"));

// Lazy load compare page
const ComparePage = lazy(() => import("./pages/ComparePage"));

// Lazy load product pages
const ScrivenerMcpPage = lazy(() => import("./pages/ScrivenerMcpPage"));
const AgentsPage = lazy(() => import("./pages/AgentsPage"));
const HolographicMemoryPage = lazy(
  () => import("./pages/HolographicMemoryPage"),
);
const FacetPage = lazy(() => import("./pages/FacetPage"));
const SentinelMailPage = lazy(() => import("./pages/SentinelMailPage"));
const SentinelMailPrivacyPage = lazy(
  () => import("./pages/SentinelMailPrivacyPage"),
);

// Lazy load B2B pages
const CaseStudiesPage = lazy(() => import("./pages/CaseStudiesPage"));

// Lazy load roadmap page
const RoadmapPage = lazy(() => import("./pages/RoadmapPage"));

// Lazy load product plan page
const ProductPlanPage = lazy(() =>
  import("./pages/ProductPlanPage").then((m) => ({
    default: m.ProductPlanPage,
  })),
);

// Lazy load manifesto page
const ManifestoPage = lazy(() => import("./pages/ManifestoPage"));
const ColophonPage = lazy(() => import("./pages/ColophonPage"));
const EssayCoachPage = lazy(() => import("./pages/EssayCoachPage"));

// Waitlist / early-access signup (captures emails to Supabase via capture-lead)
const JoinPage = lazy(() => import("./pages/JoinPage"));

// Lazy load company pages
const CareersPage = lazy(() => import("./pages/CareersPage"));
const DiversityPage = lazy(() => import("./pages/DiversityPage"));
const PressPage = lazy(() => import("./pages/PressPage"));
const PartnershipsPage = lazy(() => import("./pages/PartnershipsPage"));

// Lazy load pitch deck
const PitchDeckPage = lazy(() => import("./pages/decks/PitchDeckPage"));

// Lazy load product catalog supplemental deck
const ProductCatalogPage = lazy(
  () => import("./pages/decks/ProductCatalogPage"),
);

// Lazy load strategy team presentation
const StrategyTeamPage = lazy(() => import("./pages/decks/StrategyTeamPage"));

// Lazy load strategy growth presentation
const StrategyGrowthPage = lazy(
  () => import("./pages/decks/StrategyGrowthPage"),
);

// Lazy load segment landing pages
const IIWLandingPage = lazy(() => import("./pages/landing/IIWLandingPage"));
const AuthorMediaLandingPage = lazy(
  () => import("./pages/AuthorMediaLandingPage"),
);

// Loading fallback - minimal, fast
function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

function PageLoader() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: "var(--surface-dark, #0a0a0f)" }}
    >
      <LoadingSpinner size="lg" label="Loading page" />
    </div>
  );
}

// Route-level error boundary + Suspense wrapper for lazy-loaded pages.
// Resets the error boundary on navigation via React Router's location key.
function LazyRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <RouteErrorBoundary resetKey={location.key}>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </RouteErrorBoundary>
  );
}

// Scroll to top and move focus on route change (WCAG 2.1 compliance)
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const main = document.getElementById("main-content");
    if (main) {
      main.setAttribute("tabindex", "-1");
      main.focus({ preventScroll: true });
    }
  }, [pathname]);
  return null;
}

// Analytics wrapper - tracks page views and engagement
function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  // Initialize analytics on mount
  useEffect(() => {
    initializeAnalytics();
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (typeof document !== "undefined") {
      trackPageView(location.pathname, document.title);
    }
  }, [location.pathname]);

  // Track engagement time
  useEngagementTracking();

  // Track scroll depth - hook handles conditional logic internally
  const isLandingPage =
    location.pathname === "/" ||
    location.pathname === "/writerslogic" ||
    location.pathname.startsWith("/for-");
  useScrollDepthTracking(isLandingPage);

  return <>{children}</>;
}

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <IconSprite />
      </Suspense>
      <Suspense fallback={null}>
        <ToastContainer />
      </Suspense>
      <Suspense fallback={null}>
        <ConfirmDialog />
      </Suspense>
      <Suspense fallback={null}>
        <OfflineBanner />
      </Suspense>
      <BrowserRouter>
        <ScrollToTop />
        <AnalyticsProvider>
          <Routes>
            {/* External redirects — outside layout to avoid stale breadcrumbs */}
            <Route
              path="/accuracy"
              element={
                <ExternalRedirect to="https://writersproof.com/accuracy" />
              }
            />
            <Route
              path="/sample-reports"
              element={
                <ExternalRedirect to="https://writersproof.com/sample-reports" />
              }
            />
            <Route
              path="/for-students"
              element={
                <ExternalRedirect to="https://writersproof.com/for-students" />
              }
            />
            <Route
              path="/for-authors"
              element={
                <ExternalRedirect to="https://writersproof.com/for-authors" />
              }
            />
            <Route
              path="/for-academics"
              element={
                <ExternalRedirect to="https://writersproof.com/for-academics" />
              }
            />
            <Route
              path="/for-creators"
              element={
                <ExternalRedirect to="https://writersproof.com/for-creators" />
              }
            />
            <Route
              path="/for-enterprise"
              element={
                <ExternalRedirect to="https://writersproof.com/for-enterprise" />
              }
            />
            <Route
              path="/for-legal"
              element={
                <ExternalRedirect to="https://writersproof.com/for-legal" />
              }
            />
            <Route
              path="/for-publishers"
              element={
                <ExternalRedirect to="https://writersproof.com/for-publishers" />
              }
            />
            <Route
              path="/for-institutions"
              element={
                <ExternalRedirect to="https://writersproof.com/for-institutions" />
              }
            />
            <Route
              path="/releases"
              element={
                <ExternalRedirect to="https://writersproof.com/download" />
              }
            />
            <Route
              path="/join"
              element={
                <LazyRoute>
                  <JoinPage />
                </LazyRoute>
              }
            />
            <Route
              path="/waitlist"
              element={
                <LazyRoute>
                  <JoinPage />
                </LazyRoute>
              }
            />
            <Route
              path="/subscribe"
              element={
                <LazyRoute>
                  <JoinPage />
                </LazyRoute>
              }
            />
            <Route
              path="/proof"
              element={<ExternalRedirect to="https://writersproof.com/proof" />}
            />
            <Route
              path="/authors-guild"
              element={
                <ExternalRedirect to="https://writersproof.com/authors-guild" />
              }
            />
            <Route
              path="/proof-vs-detection"
              element={
                <ExternalRedirect to="https://writersproof.com/proof-vs-detection" />
              }
            />
            <Route
              path="/glossary"
              element={
                <ExternalRedirect to="https://writersproof.com/glossary" />
              }
            />
            <Route
              path="/forstudents"
              element={
                <ExternalRedirect to="https://writersproof.com/for-students" />
              }
            />
            <Route
              path="/forauthors"
              element={
                <ExternalRedirect to="https://writersproof.com/for-authors" />
              }
            />
            <Route
              path="/foracademics"
              element={
                <ExternalRedirect to="https://writersproof.com/for-academics" />
              }
            />
            <Route
              path="/forcreators"
              element={
                <ExternalRedirect to="https://writersproof.com/for-creators" />
              }
            />
            <Route
              path="/forenterprise"
              element={
                <ExternalRedirect to="https://writersproof.com/for-enterprise" />
              }
            />
            <Route
              path="/forlegal"
              element={
                <ExternalRedirect to="https://writersproof.com/for-legal" />
              }
            />
            <Route
              path="/forpublishers"
              element={
                <ExternalRedirect to="https://writersproof.com/for-publishers" />
              }
            />
            <Route
              path="/forprofessionals"
              element={
                <ExternalRedirect to="https://writersproof.com/for-creators" />
              }
            />
            <Route
              path="/forinstitutions"
              element={
                <ExternalRedirect to="https://writersproof.com/for-institutions" />
              }
            />
            <Route
              path="/writers-proof"
              element={<ExternalRedirect to="https://writersproof.com/proof" />}
            />
            <Route
              path="/authorsguild"
              element={
                <ExternalRedirect to="https://writersproof.com/authors-guild" />
              }
            />
            <Route
              path="/writersproof"
              element={<ExternalRedirect to="https://writersproof.com" />}
            />
            <Route
              path="/longhand"
              element={<ExternalRedirect to="https://writersproof.com/story" />}
            />
            <Route
              path="/witnessd"
              element={
                <ExternalRedirect to="https://writersproof.com/download" />
              }
            />
            <Route
              path="/download"
              element={
                <ExternalRedirect to="https://writersproof.com/download" />
              }
            />
            <Route
              path="/downloads"
              element={
                <ExternalRedirect to="https://writersproof.com/download" />
              }
            />
            <Route
              path="/proof/download"
              element={
                <ExternalRedirect to="https://writersproof.com/download" />
              }
            />
            <Route
              path="/app"
              element={
                <ExternalRedirect to="https://writersproof.com/dashboard" />
              }
            />
            <Route path="/contact" element={<Navigate to="/about" replace />} />

            {/* All public pages share WritersLogic header/footer */}
            <Route element={<WritersLogicLayout />}>
              {!IS_DOCS_HOST && (
                <Route
                  path="/"
                  element={
                    <LazyRoute>
                      <HomePage />
                    </LazyRoute>
                  }
                />
              )}
              <Route
                path="/writerslogic"
                element={
                  <LazyRoute>
                    <LandingPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/authormedia"
                element={
                  <LazyRoute>
                    <AuthorMediaLandingPage show="authormedia" />
                  </LazyRoute>
                }
              />
              <Route
                path="/novelmarketing"
                element={
                  <LazyRoute>
                    <AuthorMediaLandingPage show="novelmarketing" />
                  </LazyRoute>
                }
              />
              {BLOG_ENABLED && (
                <>
                  <Route
                    path="/blog"
                    element={
                      <LazyRoute>
                        <BlogIndex />
                      </LazyRoute>
                    }
                  />
                  <Route
                    path="/blog/:slug"
                    element={
                      <LazyRoute>
                        <BlogPost />
                      </LazyRoute>
                    }
                  />
                </>
              )}
              <Route
                path="/privacy"
                element={
                  <LazyRoute>
                    <PrivacyPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/terms"
                element={
                  <LazyRoute>
                    <TermsPage />
                  </LazyRoute>
                }
              />
              <Route
                path={docsTo("/docs")}
                element={
                  <LazyRoute>
                    <DocsPage />
                  </LazyRoute>
                }
              />
              <Route
                path={docsTo("/docs/:slug")}
                element={
                  <LazyRoute>
                    <DocsPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/soft-bindings/:slug"
                element={
                  <LazyRoute>
                    <DocsPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/soft-binding/:slug"
                element={
                  <LazyRoute>
                    <DocsPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/about"
                element={
                  <LazyRoute>
                    <AboutPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/manifesto"
                element={
                  <LazyRoute>
                    <ManifestoPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/colophon"
                element={
                  <LazyRoute>
                    <ColophonPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/essay-coach"
                element={
                  <LazyRoute>
                    <EssayCoachPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/iiw"
                element={
                  <LazyRoute>
                    <IIWLandingPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/compliance"
                element={
                  <LazyRoute>
                    <ComplianceCheckpointPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/executive-summary"
                element={
                  <LazyRoute>
                    <ExecutiveSummaryPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/sample-authorship-report"
                element={
                  <LazyRoute>
                    <SampleAuthorshipReportPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/loi"
                element={
                  <LazyRoute>
                    <LOIPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/mou"
                element={
                  <LazyRoute>
                    <MOUPage />
                  </LazyRoute>
                }
              />
              <Route path="/moi" element={<Navigate to="/mou" replace />} />
              <Route
                path="/chronograph"
                element={
                  <LazyRoute>
                    <ChronographLandingPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/licensing"
                element={
                  <LazyRoute>
                    <LicensingPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/developers"
                element={
                  <LazyRoute>
                    <DevelopersPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/protocol"
                element={
                  <LazyRoute>
                    <ProtocolPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/security-model"
                element={
                  <LazyRoute>
                    <SecurityModelPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/zwc-watermark"
                element={
                  <LazyRoute>
                    <ZwcWatermarkPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/verify"
                element={
                  <LazyRoute>
                    <ArkVerifyPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/sign"
                element={
                  <LazyRoute>
                    <SignPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/callback"
                element={
                  <LazyRoute>
                    <CallbackPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/.well-known/c2pa"
                element={
                  <LazyRoute>
                    <CaRepositoryPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/compare"
                element={
                  <LazyRoute>
                    <ComparePage />
                  </LazyRoute>
                }
              />
              <Route
                path="/scrivener-mcp"
                element={
                  <LazyRoute>
                    <ScrivenerMcpPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/agents"
                element={
                  <LazyRoute>
                    <AgentsPage />
                  </LazyRoute>
                }
              />
              <Route path="/holographic-memory/*" element={<LazyRoute><HolographicMemoryPage /></LazyRoute>} />
              <Route
                path="/warm/*"
                element={
                  <LazyRoute>
                    <HolographicMemoryPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/facet"
                element={
                  <LazyRoute>
                    <FacetPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/sentinelmail"
                element={
                  <LazyRoute>
                    <SentinelMailPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/sentinelmail/privacy"
                element={
                  <LazyRoute>
                    <SentinelMailPrivacyPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/onrecord-studio"
                element={<Navigate to="/onrecord" replace />}
              />
              <Route
                path="/onrecord"
                element={
                  <LazyRoute>
                    <OnRecordPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/pricing"
                element={
                  <LazyRoute>
                    <PricingPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/case-studies"
                element={
                  <LazyRoute>
                    <CaseStudiesPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/roadmap"
                element={
                  <LazyRoute>
                    <RoadmapPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/product-plan"
                element={
                  <LazyRoute>
                    <ProductPlanPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/careers"
                element={
                  <LazyRoute>
                    <CareersPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/diversity"
                element={
                  <LazyRoute>
                    <DiversityPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/press"
                element={
                  <LazyRoute>
                    <PressPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/partnerships"
                element={
                  <LazyRoute>
                    <PartnershipsPage />
                  </LazyRoute>
                }
              />
              <Route
                path="*"
                element={
                  <LazyRoute>
                    <NotFoundPage />
                  </LazyRoute>
                }
              />
            </Route>

            {/* Pitch deck — fullscreen, outside layout */}
            <Route
              path="/pitchdeck"
              element={
                <LazyRoute>
                  <PitchDeckPage />
                </LazyRoute>
              }
            />

            {/* Product catalog — fullscreen, outside layout */}
            <Route
              path="/productcatalog"
              element={
                <LazyRoute>
                  <ProductCatalogPage />
                </LazyRoute>
              }
            />

            {/* Strategy team presentation — fullscreen, outside layout */}
            <Route
              path="/strategy-team"
              element={
                <LazyRoute>
                  <StrategyTeamPage />
                </LazyRoute>
              }
            />

            {/* Strategy growth presentation — fullscreen, outside layout */}
            <Route
              path="/strategy-growth"
              element={
                <LazyRoute>
                  <StrategyGrowthPage />
                </LazyRoute>
              }
            />
            <Route element={<WritersLogicLayout />}>
              <Route
                path="/fhe-lite-demo"
                element={
                  <LazyRoute>
                    <FheLiteDemoPage />
                  </LazyRoute>
                }
              />
            </Route>
              </Routes>
        </AnalyticsProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
